import { useEffect, useMemo, useState } from "react";
import publicApi from "../../lib/publicApi";
import { useCart } from "../../context/CartContext";
import { formatMYR } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { Search, Package, ShoppingBag } from "lucide-react";

export default function ShopHome() {
    const [products, setProducts] = useState([]);
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const { add } = useCart();

    useEffect(() => {
        publicApi.get("/public/products")
            .then(({ data }) => setProducts(data))
            .catch(() => toast.error("加载商品失败"))
            .finally(() => setLoading(false));
    }, []);

    const filtered = useMemo(() => {
        if (!q) return products;
        const t = q.toLowerCase();
        return products.filter((p) =>
            (p.name || "").toLowerCase().includes(t) ||
            (p.category || "").toLowerCase().includes(t) ||
            (p.sku || "").toLowerCase().includes(t)
        );
    }, [products, q]);

    const addToCart = (p) => {
        add(p, 1);
        toast.success(`已加入购物车：${p.name}`);
    };

    return (
        <div className="space-y-8" data-testid="shop-home">
            <div className="text-center space-y-3 py-6">
                <div className="text-xs tracking-widest uppercase text-gold-700">Fine Jewelry · 精品珠宝</div>
                <h1 className="text-4xl sm:text-5xl font-black tracking-tight text-slate-900">
                    缘起福生 · <span className="text-gold-600">智启未来</span>
                </h1>
                <p className="text-sm text-slate-600 max-w-xl mx-auto">
                    每一件饰品都是一份福缘。精选设计，匠心呈现。
                </p>
            </div>

            <div className="max-w-md mx-auto relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gold-400" />
                <Input
                    data-testid="shop-search-input"
                    placeholder="搜索产品名称、SKU 或分类..."
                    className="pl-9 bg-white/70 border-gold-200"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                />
            </div>

            {loading ? (
                <div className="text-center py-16 text-gold-500">加载中...</div>
            ) : filtered.length === 0 ? (
                <div className="text-center py-16">
                    <Package className="h-12 w-12 mx-auto text-gold-300 mb-3" />
                    <div className="text-sm text-gold-500">{products.length === 0 ? "暂无在售商品" : "没有匹配的商品"}</div>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filtered.map((p) => (
                        <div
                            key={p.id}
                            data-testid={`shop-product-${p.id}`}
                            className="group rounded-xl overflow-hidden bg-white/80 backdrop-blur-sm border border-gold-200 shadow-sm hover:shadow-lg hover:border-gold-400 transition-all duration-200"
                        >
                            <div className="aspect-square bg-gold-50 overflow-hidden relative">
                                {p.image_url ? (
                                    <img
                                        src={p.image_url}
                                        alt={p.name}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                        draggable="false"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-gold-300">
                                        <Package className="h-16 w-16" />
                                    </div>
                                )}
                                {p.stock <= 3 && (
                                    <div className="absolute top-2 right-2 bg-red-600 text-white text-xs font-medium px-2 py-1 rounded-full">仅剩 {p.stock}</div>
                                )}
                            </div>
                            <div className="p-5 space-y-3">
                                <div>
                                    {p.category && <div className="text-[10px] tracking-widest uppercase text-gold-600">{p.category}</div>}
                                    <h3 className="font-semibold text-slate-900 mt-1 truncate">{p.name}</h3>
                                    {p.description && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{p.description}</p>}
                                </div>
                                <div className="flex items-end justify-between">
                                    <div className="font-mono text-xl font-bold text-gold-700">{formatMYR(p.unit_price)}</div>
                                    <Button
                                        data-testid={`shop-add-${p.id}`}
                                        size="sm"
                                        onClick={() => addToCart(p)}
                                        className="bg-gold-600 hover:bg-gold-700 text-white gap-1.5"
                                    >
                                        <ShoppingBag className="h-3.5 w-3.5" /> 加入
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
